import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import { sortBy, map, isEmpty } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import itemService from '../../services/itemService';
import { toast } from 'react-toastify';

const ProductByEvent = ({ open, onClose, id }) => {
  const [items, setItems] = useState([]);
  const [eventItems, setEventItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchItems = async () => {
    try {
      const response = await itemService.getItems();
      setItems(sortBy(response.data, 'item_name'));
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load products');
    }
  };

  const fetchItemsByEvent = async () => {
    try {
      const response = await itemService.getItemsByEventId(id);
      setEventItems(response.data);
      if (!isEmpty(response.data)) {
        setSelectedItems(map(response.data, 'id'));
      } else {
        setSelectedItems([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (open) {
      fetchItems();
      fetchItemsByEvent();
      setSearchTerm('')
    }
  }, [open]);

  const handleToggle = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.warning('Please select at least one product');
      return;
    }

    setLoading(true);
    try {
      await itemService.assignProducts(
        {
          product_ids: selectedItems,
        },
        id
      );
      toast.success('Products submitted successfully');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const SECTION_COLUMNS = 2;
  const getColumns = (sectionItems, columnCount = SECTION_COLUMNS) =>
    Array.from({ length: columnCount }, (_, colIndex) =>
      sectionItems.filter((_, itemIndex) => itemIndex % columnCount === colIndex)
    );
  const routeItems = items.filter(
    (item) => item?.station_type?.toUpperCase() === 'ROUTE'
  );
  const junctionItems = items.filter(
    (item) => item?.station_type?.toUpperCase() === 'JUNCTION'
  );
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRouteItems = routeItems.filter((item) =>
    item?.item_name?.toLowerCase().includes(normalizedSearch)
  );
  const filteredJunctionItems = junctionItems.filter((item) =>
    item?.item_name?.toLowerCase().includes(normalizedSearch)
  );

  const allSelected = selectedItems.length === items.length && items.length > 0;
  const partiallySelected =
    selectedItems.length > 0 && selectedItems.length < items.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map((item) => item.id));
    }
  };

  const renderSection = (title, sectionItems, sectionKey) => (
    <Paper
      variant='outlined'
      sx={{
        borderRadius: 2,
        borderColor: '#e6e6e6',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {sectionItems.length} items
        </Typography>
      </Box>
      <Divider />

      <Box sx={{ p: 1, maxHeight: 360, overflowY: 'auto' }}>
        {sectionItems.length ? (
          <Grid container spacing={1.5}>
            {getColumns(sectionItems).map((column, colIndex) => (
              <Grid item xs={6} key={`${sectionKey}-${colIndex}`}>
                {column.map((item) => (
                  <ListItem
                    key={item.id}
                    dense
                    button
                    onClick={() => handleToggle(item.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      px: 1,
                      '&:hover': { backgroundColor: '#fff6f4' },
                    }}
                  >
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      sx={{ p: 0, pr: 1 }}
                    />
                    <ListItemText
                      primary={item.item_name}
                      primaryTypographyProps={{
                        fontSize: '0.7rem',
                        lineHeight: 1.25,
                      }}
                    />
                  </ListItem>
                ))}
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant='body2' sx={{ color: 'text.secondary', p: 1 }}>
            No products available.
          </Typography>
        )}
      </Box>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between',alignItems:'center', pb: 0.4 }}
      >
        <span style={{fontSize:"0.9rem", fontWeight:'600'}}>Select Products</span>
        <Box>
          <ListItem dense button onClick={handleSelectAll}>
            <Checkbox
              checked={allSelected}
              indeterminate={partiallySelected}
              sx={{ p: 0, pr: 1, color: 'red' }}
            />
            <ListItemText primary='Select All' sx={{ color: 'red' }} />
          </ListItem>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          size='small'
          placeholder='SEARCH PRODUCTS BY NAME...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2, textTransform: 'capitalize' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderSection('Route Products', filteredRouteItems, 'route')}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSection('Junction Products', filteredJunctionItems, 'junction')}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: 'space-between', padding: '8px 18px' }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{ color: '#be210b', borderColor: '#be210b' }}
        >
          CANCEL
        </Button>

        <Button
          variant='contained'
          onClick={handleSubmit}
          sx={{ background: '#be210b' }}
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductByEvent;
